import {Injectable} from "@angular/core";
import firebase from "firebase/app";
import {AngularFirestore} from "@angular/fire/firestore";
import {map, mergeMap} from 'rxjs/operators';
import {rejects} from "assert";
import {Headers, Http, RequestOptions} from "@angular/http";
import {AuthProvider} from "../auth/auth";
import {ENV} from '@app/env'
import _ from 'lodash';
import {Observable, Subject} from "rxjs";
import DocumentSnapshot = firebase.firestore.DocumentSnapshot;
import * as moment from "moment";
import CollectionReference = firebase.firestore.CollectionReference;
import Timestamp = firebase.firestore.Timestamp;
import {take} from "rxjs/operators";
import { Rx } from "models/rx";

export interface Other {
  userId: string;
  createdAt: Date;
  image: string;
  content: string;
  [key: string]: any;
}

export interface clinic {
  id?: string;
  clinic_id: string;
  clinic_name: string;
  createdAt: any;
  updatedAt: any;
}

export interface group {
  id?: string;
  group_name: string;
  description: string;
}

export interface medication {
  id?: string;
  din: string;
  name: string;
  type: string;
  createdAt: any;
  updatedAt: any;
  logo?: string;
  category?: string;
  sort?: number;
}

export interface doctor {
  id?: string;
  first_name: string;
  last_name: string;
  license: string;
  createdAt: any;
  updatedAt: any;
}

export interface patient {
  id?: string;
  full_name: string;
  first_name: string;
  last_name: string;
  email: string;
  healthcard: string;
  phone_number: string;
  address: string;
  address2: string;
  city: string;
  postal_code: string;
  province: string;
  gender: string;
  dob: string;
  createdAt: any;
  updatedAt: any;
  group: any;
}

@Injectable()
export class DatabaseProvider {
  constructor(private afs: AngularFirestore, public http: Http, public auth: AuthProvider) {}

  /*********CLINICS **********************************/
  // Get All Clinics
  getClinics() {
    return this.afs.collection<clinic>("clinics", ref =>
      ref
        .orderBy("clinic_name", "asc")
    );
  }

  // Get Single Clinic
  getClinic(id) {
    return this.afs.collection<clinic>(`clinics`).doc(id);
  }

  createClinic(data: clinic, user) {
    const id = this.afs.createId();
    const createdAt = firebase.firestore.FieldValue.serverTimestamp();
    const createdBy = user;
    const updatedAt = createdAt;
    const updatedBy = user;
    const doc = { createdAt, createdBy, updatedAt, updatedBy, ...data };
    return this.afs.collection<clinic>("clinics").add(doc);
  }

  updateClinic(id: string, data: clinic, user)  {
    const updatedAt = firebase.firestore.FieldValue.serverTimestamp();
    const updatedBy = user;
    const doc = { updatedAt, updatedBy, ...data };
    return this.afs.collection<clinic>("clinics").doc(id).update(doc);
  }

  /********* SUPPLIES **********************************/
  getSupplies() {
    return this.afs.collection<medication>("supplies", ref => 
      ref
      .orderBy("name", "asc")
    );
  }

  getDefaultSuppliesByClinic(clinic_id)  {
    return this.afs.collection("supplies", ref => 
      ref
        .where("clinics_default", "array-contains", clinic_id)
        .orderBy("sort", "asc")
    );
  }

  getDefaultSuppliesByDoctor(doctor_id)  {
    // console.log(doctor_id);
    return this.afs.collection("supplies", ref => 
      ref
        .where("doctors_default", "array-contains", doctor_id)
        .orderBy("sort", "asc")
    )
  }

  deleteSupply(supply_din)  {
    var batch = this.afs.firestore.batch();
    const supplyRef = this.afs.doc(`supplies/${supply_din}`).ref;
    batch.delete(supplyRef);
    return batch.commit();
  }

  async addSupply(supply)  {
    const createdAt = firebase.firestore.FieldValue.serverTimestamp();
    const id = this.afs.createId();
    const updatedAt = createdAt;
    supply.din = id;
    const doc = { id: id, createdAt, updatedAt, ...supply };
    return await this.afs
    .collection(`supplies`)
    .doc(id)
    .set(doc);
  }

  async updateSupply(supply)  {
    const updatedAt = firebase.firestore.FieldValue.serverTimestamp();
    const doc = { updatedAt, ...supply };
    return await this.afs
    .collection(`supplies`)
    .doc(supply.din)
    .set(doc);
  }

  async addSupplyByClinic(clinic_id, supply)  {
    var batch = this.afs.firestore.batch();
    // batch.delete(patientRef);

    const createdAt = firebase.firestore.FieldValue.serverTimestamp();
    const updatedAt = createdAt;
    const doc = { createdAt, updatedAt, ...supply };
    const clinicSuppliesRef = this.afs.doc(`clinics/${clinic_id}/supplies/${supply.din}`).ref;
    batch.set(clinicSuppliesRef , doc);

    return batch.commit();
  }

  async updateSupplyByClinic(clinic_id, supply)  {
    const updatedAt = firebase.firestore.FieldValue.serverTimestamp();
    const doc = { updatedAt, ...supply };
    return await this.afs
    .collection(`clinics/${clinic_id}/supplies`)
    .doc(supply.din)
    .update(doc);
  }

  async deleteSupplyByClinic(clinic_id, supply_din) {
    return await this.afs
    .collection(`clinics/${clinic_id}/supplies`)
    .doc(supply_din)
    .delete();
  }

  getSupplyByClinic(clinic_id)  {
    return this.afs.collection("supplies", ref => 
      ref
        .where("clinics", "array-contains", clinic_id)
        .orderBy("name", "asc")
    );
  }

  /********* DOCTORS **********************************/
  getDoctorsByClinic(clinic_id) {
    return this.afs.collection<doctor>(`clinics/${clinic_id}/doctors`, ref =>
      ref.orderBy("last_name", "asc")
    );
  }

  updateDoctor(clinic_id, doctor_id, data: doctor) {
    const updatedAt = this.timestamp;
    const doc = { updatedAt, ...data };

    return this.afs.doc(`clinics/${clinic_id}/doctors/${doctor_id}`).update(doc);
  }

  addDoctor(clinic_id, data) {
    const createdAt = this.timestamp;
    const updatedAt = createdAt;
    const doc = { createdAt, updatedAt, ...data };

    return this.afs.collection(`clinics/${clinic_id}/doctors`).add(doc);
  }

  deleteDoctor(clinic_id, doctor_id) {
    var batch = this.afs.firestore.batch();
    const doctorRef = this.afs.doc(`clinics/${clinic_id}/doctors/${doctor_id}`).ref;
    batch.delete(doctorRef);
    return batch.commit();
  }

  /********* ORDERS **********************************/
  getOrder(order_id: string) {
    return this.afs.doc(`orders/${order_id}`);
  }

  async saveOrder(order: any, isRx?: boolean) {

    // Create Master Order Summary and Write to Root and Clinic
    const id = this.afs.createId();
    order.id = id;
    order.status = "Queued";
    order.createdAt = this.timestamp;

    // Create transaction batch - Batch Max 500
    let batch = this.afs.firestore.batch();

    // Add master order - Batch Max 499
    const masterOrderDocRef = this.afs.doc(`orders/${id}`).ref;
    batch.set(masterOrderDocRef, order);

    // Add master order to clinic collection - Batch Max 498
    const clinicRef = this.afs.doc(`clinics/${order.clinic_id}/orders/${id}`).ref;
    batch.set(clinicRef , order);

    // Get Rx by Clinic and User and add to batch - will fail if there are more than 497 prescriptions in the batch
    if (isRx) {
      this.getPrescriptionBatchByClinicAndUser(order.clinic_id, order.email)
      .valueChanges()
      .take(1)
      .subscribe(rxs=>{
          console.log("Rx to process: ", rxs, order.email)
          // const writeBatch = this.afs.firestore.batch();
          rxs.forEach((rx) => {
            batch.set(this.afs.doc(`prescriptions/${rx.rx_id}`).ref, { status: 'Queued', order_number: order.order_number, order_number_master: order.order_number_master, order: { id: id, number: order.order_number } } , {merge: true} );
          })
          return batch.commit();
       });
    } else {
      return batch.commit();
    }
  }

  getOrdersbyClinic(clinic_id: string) {
    return this.afs.collection<any>(`clinics/${clinic_id}/orders`, ref =>
      ref.orderBy("createdAt", "asc")
    );
  }

  getOrdersbyClinicWithDateRange(clinic_id: string, start_date: Timestamp, end_date: Timestamp) {
    return this.afs.collection<any>(`clinics/${clinic_id}/orders`, ref =>
      ref.where("createdAt", ">=", start_date)
        .where("createdAt", "<=", end_date)
        .where('type', '==', 'rx')
        .orderBy("createdAt", "asc")
    );
  }

  async getPrescriptionsByPatientFilledCount(clinic_id: string, patient_id: string) {
    const docs = await this.afs.collection<any>(`prescriptions`, ref =>
      ref.where("clinic_id", "==", clinic_id)
        .where("patient_id", "==", patient_id)
        .where("filled", "==", true)
        .orderBy("createdAt", "desc")
        .limitToLast(1)
      )
    .valueChanges()
    .pipe(take(1))
    .toPromise();

    return docs;
  }

  async getOrdersByPatientBatchedCount(clinic_id: string, patient_id: string) {
    const docs = await this.afs.collection<any>(`prescriptions`, ref =>
      ref.where("clinic_id", "==", clinic_id)
        .where("patient_id", "==", patient_id)
        .where("status", "==", "Batch")
      )
    .valueChanges()
    .pipe(take(1))
    .toPromise();

    return docs ? docs.length : 0
  }

  getOrdersByTerritory(territory, limit, lastElement: DocumentSnapshot) {
    if (lastElement) {
      if(territory) {
        return this.afs.collection<any>(`orders`, ref =>
          ref
            .where("clinic_province", "==", territory)
            .orderBy("createdAt", "desc")
            .limit(limit)
            .startAfter(lastElement)
        )
      } else {
        return this.afs.collection<any>(`orders`, ref =>
          ref
            .orderBy("createdAt", "desc")
            .limit(limit)
            .startAfter(lastElement)
        )
      }
    } else {
      if(territory) {
        return this.afs.collection<any>(`orders`, ref =>
          ref
            .where("clinic_province", "==", territory)
            .orderBy("createdAt", "desc")
            .limit(limit)
        )
      } else {
        return this.afs.collection<any>(`orders`, ref =>
          ref
            .orderBy("createdAt", "desc")
            .limit(limit)
        )
      }
    }
  }

  /********* Prescriptions **********************************/
  deletePrescription(rx_id) {
    return this.afs.doc(`prescriptions/${rx_id}`).ref.delete();
  }

  getPrescriptionsByClinic(clinic_id: string) {
    return this.afs.collection<any>(`prescriptions`, ref => {
      return ref
        .where("clinic_id", "==", clinic_id)
        .orderBy('createdAt', "desc")
    })
  }

  getPrescriptionBatchByClinic(clinic_id: string, sort?: string | null, direction?: any | null) {
    return this.afs.collection<any>(`prescriptions`, ref => {
      let query = ref
        .where("clinic_id", "==", clinic_id)
        .where("status", "==", 'Batch')

      if (sort) return query.orderBy(sort, direction).orderBy('createdAt', 'desc');
      return query.orderBy('createdAt', "desc")
    })
  }

  getPrescriptionByClinicTobra(clinic_id: string, sort?: string | null, direction?: any | null) {
    return this.afs.collection<any>(`prescriptions`, ref => {
      let query = ref
        .where("clinic_id", "==", clinic_id)
        .where("tobra", "==", 'Tobramycin 1-2 GTTS AE Q4H x 3D POST INJ')

      if (sort) return query.orderBy(sort, direction).orderBy('createdAt', 'desc');
      return query.orderBy('createdAt', "desc")
    })
  }

  getPrescriptionBatchByClinicAndOrderNumber(clinic_id: string, order_number: string, sort?: string | null, direction?: any | null) {
    console.log("GetRx Clinic - Order", clinic_id, order_number);
    return this.afs.collection<Rx>(`prescriptions`, ref => {
      let query = ref
        .where("clinic_id", "==", clinic_id)
        .where("order_number", "==", order_number)

      if (sort) return query.orderBy(sort, direction).orderBy('createdAt', 'desc');
      return query.orderBy('createdAt', "desc")
    })
  }

  getPrescriptionBatchByClinicAndUser(clinic_id: string, user_email: string, sort?: string | null, direction?: any | null) {
    return this.afs.collection<Rx>(`prescriptions`, ref => {
      let query = ref
        .where("clinic_id", "==", clinic_id)
        .where("createdBy", "==", user_email)
        .where("status", "==", 'Batch')

      if (sort) return query.orderBy(sort, direction).orderBy('createdAt', 'desc');
      return query.orderBy('createdAt', "desc")
    })
  }

  async deletePrescriptionBatchByClinic(clinic_id: string)  {
    const batchList: Observable<firebase.firestore.QuerySnapshot> = await this.afs.collection('prescriptions', ref => ref.where('clinic_id', '==', clinic_id).where('status', '==', 'Batch')).get();
    
    const batch = this.afs.firestore.batch();
    batchList.pipe(
      mergeMap(rx => rx.docs),
      map((rx: firebase.firestore.QueryDocumentSnapshot) => batch.delete(rx.ref))
    ).toPromise().then(() => batch.commit());    
  }

  updatePrescriptionPatientInfo(rx_id, patient) {
    return this.afs.doc(`prescriptions/${rx_id}`).ref.update({ patient: patient });
  }

  getPrescriptions(province, is_pharmacist_or_admin, clinic_id, filled) {
    if (!is_pharmacist_or_admin) { //clinic user
      return this.afs.collection<any>(`prescriptions`, ref => {
        let query = ref
          .where("clinic_id", "==", clinic_id)
          .where("filled", "==", filled)
          // .where("status", "==", 'Queued')
          // .where("status", "==", 'In Progress');
        if (filled) {
          return query.orderBy('filled_date', "desc")
        } else {
          return query.orderBy("createdAt", "desc");
        }
      })
    } else if (province) { //pharmacist or reporting
      return this.afs.collection<any>("prescriptions", ref => {
        let query = ref
          .where("clinic_province", "==", province)
          .where("filled", "==", filled)
          // .where("status", "==", 'Queued')
          // .where("status", "==", 'In Progress');
        if (filled) {
          return query.orderBy('filled_date', "desc")
        } else {
          return query.orderBy("createdAt", "desc");
        }
      })
    } else { //retina admin
      console.log('Retina Admin Prescription Search', filled);
      return this.afs.collection<any>(`prescriptions`, ref => {
        let query = ref.where("filled", "==", filled)
          // .where("status", "==", 'Queued')
          // .where("status", "==", 'In Progress');
        if (filled) {
          return query.orderBy('filled_date', "desc")
        } else {
          return query.orderBy("createdAt", "desc");
        }
      })
    }
  }

  getArchivedPrescriptions(province, is_pharmacist_or_admin, clinic_id, lastPrescription, limit, searchFilter) {
    return this.afs.collection<any>(`prescriptions_archive`, ref => {
      let query: firebase.firestore.Query | CollectionReference = ref;
      if (!is_pharmacist_or_admin) {
        query = query.where("clinic_id", "==", clinic_id)
      } else if (province) {
        query = query.where("clinic_province", "==", province)
      }

      query = query.orderBy("archived_date", "desc");

      if (searchFilter === "") {
        query = query.limit(limit);
      }

      if (lastPrescription) {
        query = query.startAfter(lastPrescription)
      }
      return query;
    })
  }

  getPrescription(rx_id: string) {
    return this.afs.doc(`prescriptions/${rx_id}`);
  }

  togglePrescriptionFilled(rx_id: string, filled: boolean) {
    const filled_date = filled? this.timestamp: firebase.firestore.FieldValue.delete();
    return this.afs.doc(`prescriptions/${rx_id}`).set({filled: filled, filled_date: filled_date}, {merge: true});
  }

  togglePrescriptionArchived(id: string, archive: boolean) {
    const archived_date = archive? this.timestamp: firebase.firestore.FieldValue.delete();
    const delete_path = archive? `prescriptions/${id}`:`prescriptions_archive/${id}`;
    const add_path = !archive? `prescriptions/${id}`:`prescriptions_archive/${id}`;
    this.afs.doc(delete_path)
      .valueChanges()
      .pipe(take(1))
      .subscribe((rx: any) => {
        return Promise.all([
          this.afs.doc(add_path).ref.set({...rx, archived_date: archived_date}, { merge: true}),
          this.afs.doc(delete_path).ref.delete()
        ])
      });
  }

  bulkTogglePrescriptionsFilled(ids: string[], fill: boolean) {
    const filled_date = fill? this.timestamp: firebase.firestore.FieldValue.delete();
    let batch = this.afs.firestore.batch();
    ids.forEach((id) => {
      batch.set(this.afs.doc(`prescriptions/${id}`).ref,{filled: fill, filled_date: filled_date}, {merge: true});
    });
    batch.commit();
  }



  editPrescriptionNote(note: any, rx_id: string, user: any) {
    note.pharmacist_note_user = user;
    return this.afs.doc(`prescriptions/${rx_id}`).set(note, {merge: true});
  }


  addOrderPrintHistory(order_id: string, user: any) {
    const id = this.afs.createId();
    const createdAt = firebase.firestore.FieldValue.serverTimestamp();
    const doc = { id: id, order_id: order_id, createdAt: createdAt, user: user };
    const last_printed = { printId: id, printedAt: createdAt, printedBy: user };
    return Promise.all([
      this.afs.collection("printhistory").add(doc),
      this.afs.collection('orders').doc(order_id).set( { last_printed: last_printed }, {merge: true})
    ])
  }

  getOrderPrintHistory(order_id: string) {
    return this.afs.collection<any>(`printhistory`, ref =>
      ref.where("order_id", "==", order_id).orderBy("createdAt", "desc")
    );
  }

  /********* Patients **********************************/
  getPatientsByClinic(clinic_id) {
    return this.afs.collection<any>(`clinics/${clinic_id}/patients`, ref =>
      ref.orderBy("last_name", "asc")
    );
  }

  getPrescriptionsbyPatient(clinic_id: string, patient_id: string) {
    return this.afs.collection<any>(`clinics/${clinic_id}/patients/${patient_id}/prescriptions`, ref =>
      ref
      .orderBy("createdAt", "desc")
    );
  }

  addPatient(clinic_id, data: patient) {
    return new Promise<any>((resolve, reject) => {
      const createdAt = this.timestamp;
      const updatedAt = createdAt;
      const patientId = this.afs.createId();
      const doc = { createdAt, updatedAt, ...data };
      //const patients = this.afs.collection(`clinics/${clinic_id}/patients`);
      this.afs.collection(`clinics/${clinic_id}/patients`)
        .valueChanges()
        .pipe(take(1))
        .subscribe((patients) => {
          let valid = true;
          const sameName = patients.filter((p) => p['full_name'].toLowerCase() === data.full_name.toLowerCase());
          for (const patient of sameName) {
            if (patient['phone_number'].replace(/\D/g,'') === data.phone_number.replace(/\D/g,'') ||
              (patient['email'].length > 0 && patient['email'].toLowerCase() === data.email.toLowerCase()) ||
              data.postal_code.replace(/ /g,'').toLowerCase() === patient["postal_code"].replace(/ /g,'').toLowerCase() ||
              data.dob === patient["dob"] ||
              data.healthcard.replace(/ /g,'').toLowerCase() === patient["healthcard"].replace(/ /g,'').toLowerCase()
            ) {
              valid = false;
            }
          }

          if (valid) {
            this.afs.doc(`clinics/${clinic_id}/patients/${patientId}`).set(doc).then(() => {
              data.id = patientId;
              this.managePatientGroup(clinic_id, data);

              resolve(patientId);
            })
          } else {
            reject("This Patient Already Exists");
          }
        });
    });
  }

  getPatientFromClinic(clinic_id, patient_id) {
    console.log(clinic_id + ' - ' + patient_id)
    return this.afs.collection<any>(`clinics/${clinic_id}/patients/`).doc(patient_id);
  }

  updatePatient(clinic_id, data: any) {
    if (data.group !== data.old_group) {
      this.managePatientGroup(clinic_id, data);
    }
    const updatedAt = this.timestamp;
    const patientId = data.id;
    const doc = { updatedAt, ...data };
    return this.afs.doc(`clinics/${clinic_id}/patients/${patientId}`).update(doc);
  }

  deletePatient(clinic_id, patient_id) {
    return this.afs.doc(`clinics/${clinic_id}/patients/${patient_id}`).ref.delete();
  }

  managePatientGroup(clinic_id, patient) {
    if (patient.group && patient.group != "None")
      this.afs.collection(`clinics/${clinic_id}/groups/${patient.group.id}/patients`).doc(patient.id).set(patient);
    if (patient.old_group && patient.old_group != "None")
      this.afs.doc(`clinics/${clinic_id}/groups/${patient.old_group.id}/patients/${patient.id}`).delete();
  }

  /********* Users **********************************/
  getUsers() {
    return this.afs.collection("users", ref => 
      ref.orderBy("last_name", "asc")
    );
  }

  deleteUser(uid) {
    var headers = new Headers()
    headers.append('Content-Type', 'application/x-www-form-urlencoded');
    let options = new RequestOptions({ headers: headers });
    let body =  "secret=123456&uid=" + uid;
    return new Promise((resolve, reject) => {
       this.http.post(`${ENV.FIREBASE_API_URL}/deleteUser`, body, options)
        .pipe(map(res => res.json()))
        .subscribe(
        (data) => {
          resolve(data);
        },
        (err) => {
          reject(new Error("Could not save data to the server."));
        });
    });
  }

  createUser(user) {
    var headers = new Headers();
    headers.append('Content-Type', 'application/x-www-form-urlencoded');
    let options = new RequestOptions({ headers: headers });
    let body = 'secret=123456&email=' + user.email + '&password=' + user.password + '&first_name=' + encodeURIComponent(user.first_name) +
      '&last_name=' + encodeURIComponent(user.last_name) + '&clinic_id=' + encodeURIComponent(user.clinic_id) + '&clinic_name=' + encodeURIComponent(user.clinic_name) +
      '&clinic_province=' + encodeURIComponent(user.clinic_province) + '&retina_admin=' + user.roles.retina_admin + '&clinic_admin=' +
      user.roles.clinic_admin + '&retina_reporting=' + user.roles.retina_reporting + '&retina_pharmacist=' + user.roles.retina_pharmacist;
    return new Promise((resolve, reject) => {
       this.http.post(`${ENV.FIREBASE_API_URL}/createUser`, body, options)
       .pipe(map(res => res.json()))
       .subscribe(
       (data) => {
         resolve(data);
       },
       (err) => {
         reject(new Error("Could not save data to the server."));
       });
    });
  }

  async updateUser(user_id, data) {
    return await this.afs.collection('users').doc(user_id).set(data);
  }

  async updateUserClinic(user_id, data) {
    console.log('doing update')
    return await this.afs.collection('users').doc(user_id).set(data, {merge: true});
  }

  /********* Groups ********************************************/

  getGroups(clinic_id: string) {
    return this.afs.collection<group>(`clinics/${clinic_id}/groups`, ref =>
      ref.orderBy("group_name", "asc")
    );
  }

  getGroup(clinic_id: string, group_id: string) {
    return this.afs.doc<group>(`clinics/${clinic_id}/groups/${group_id}`);
  }

  getGroupPatients(clinic_id: string, group_id: string) {
    return this.afs.collection<any>(`clinics/${clinic_id}/groups/${group_id}/patients`);
  }

  createGroup(data: group, user, clinic_id: string) {
    const id = this.afs.createId();
    const createdAt = firebase.firestore.FieldValue.serverTimestamp();
    const createdBy = user;
    const updatedAt = createdAt;
    const updatedBy = user;
    const doc = { createdAt, createdBy, updatedAt, updatedBy, ...data };
    return this.afs.collection<group>(`clinics/${clinic_id}/groups`).add(doc);
  }

  updateGroup(data: group, id: string, user, clinic_id: string) {
    const updatedAt = firebase.firestore.FieldValue.serverTimestamp();
    const updatedBy = user;
    console.log(data);
    const doc = { updatedAt, updatedBy, ...data };
    return this.afs.collection<clinic>(`clinics/${clinic_id}/groups`).doc(id).update(doc);
  }

  removePatientFromGroup(clinic_id: string, group_id: string, patient_id: any) {
    return Observable.forkJoin({
      group: this.afs.doc(`clinics/${clinic_id}/groups/${group_id}/patients/${patient_id}`).delete(),
      patient: this.afs.doc(`clinics/${clinic_id}/patients/${patient_id}`).set({group: "None"}, {merge: true})
    })
  }

  deleteGroup(clinic_id, group_id, ngUnsubscribe: Subject<any>) {
    this.afs.collection(`clinics/${clinic_id}/groups/${group_id}/patients`).snapshotChanges()
      .map(actions => {
        return actions.map(a => {
          return a.payload.doc.id;
        });
      })
      .pipe(take(1))
      .subscribe((patient_ids) =>
      {
        console.log(patient_ids);
        patient_ids.forEach((id) => {
          this.afs.doc(`clinics/${clinic_id}/patients/${id}`).set({group: "None"}, {merge: true})
        });
      });

    return this.afs.doc(`clinics/${clinic_id}/groups/${group_id}`).delete();
  }

  /********* Nuring Homes **********************************/

  getNursingHomes() {
    return this.afs.collection("nursing_homes", ref =>
      ref.orderBy("clinic_name", "asc").orderBy("location", "asc"));
  }

  getNursingHomeByLocationAndDoctor(doctor_id, location) {
    return this.afs.collection("nursing_homes", (ref) =>
      ref.where("doctor_id", "==", doctor_id).where("location", "==", location))
  }

  createNursingHome(data) {
    const nursingHomeId = this.afs.createId();
    data.id = nursingHomeId;
    return this.afs.doc("nursing_homes/"+nursingHomeId).set(data);
  }

  deleteNursingHomesForDoctor(doctor_id) {
    console.log(doctor_id);
    this.afs.collection("nursing_homes", (ref) => ref.where("doctor_id", "==", doctor_id))
      .snapshotChanges()
      .pipe(take(1))
      .subscribe(a => {
        a.forEach((doc) => {
          doc.payload.doc.ref.delete();
        })
      })
  }

  setNursingHomeKrollToggle(toggle, id) {
    this.afs.doc("nursing_homes/"+id).update({in_kroll: toggle});
  }

  /********* Helper Functions **********************************/
  private concatIds(a: string, b: string) {
    return `${a}_${b}`;
  }

  get timestamp() {
    return firebase.firestore.FieldValue.serverTimestamp();
  }

  createId() {
    return this.afs.createId();
  }

  get dins() {
    return {
      "Eylea vial": "02415992",
      "Eylea PFS": "02505355",
      "Lucentis PFS": "02425629",
      "Lucentis vial": "02296810",
      "Avastin": "02270994",
      "Clindamycin PFS": "02230540",
      "Dexamethasone PFS": "00664227",
      "Ozurdex": "02363445",
      "Ocuflox": "02143291",
      "Beovu": "02496976",
      "Triesence": "02368676",
      "Vabysmo": "02527618"
    }
  }

  getDoc(path,document)  {
    return this.afs.collection<clinic>(path).doc(document);
  }

  setDoc(path,document_id, document) {
    return this.afs
    .collection(path)
    .doc(document_id)
    .set(document);
  }

  updateDoc(path,document_id, document) {
    return this.afs
    .collection(path)
    .doc(document_id)
    .update(document);
  }

  getCollection(path)  {
    return this.afs.collection<any>(path);
  }

  setDocWithID(path, document) {
    return new Promise<any>((resolve, reject) => {
      const createdAt = this.timestamp;
      const updatedAt = createdAt;
      const id = this.afs.createId();
      const doc = { id, createdAt, updatedAt, ...document };
      const afscoll = this.afs.collection(path)
      afscoll.doc(id).set(doc).then((setdoc) => {
        console.log(id)
        resolve(id);
      })
      .catch((err) => {
        rejects(err);
      })
    });
  }

  setDocWithExistingID(collectionName, id, document): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      delete document.createdAt;
      delete document.updatedAt;
      const createdAt = this.timestamp;
      const updatedAt = createdAt;
      const doc = { id, createdAt, updatedAt, ...document };
      const afscoll = this.afs.collection(collectionName)
      afscoll.doc(id).set(doc).then((setdoc) => {
        console.log(id)
        resolve(id);
      })
      .catch((err) => {
        reject(err);
      })
    });
  }

  incrementClinicOrderNumber(clinic_id: string){
    const incrementBy1 = firebase.firestore.FieldValue.increment(1);
    this.afs.doc("clinics/"+clinic_id).update({current_order: incrementBy1});
  }

  decrementClinicOrderNumber(clinic_id: string){
    const decrementBy1 = firebase.firestore.FieldValue.increment(-1);
    this.afs.doc("clinics/"+clinic_id).update({current_order: decrementBy1});
  }

  incrementMasterOrderNumber(){
    const incrementBy1 = firebase.firestore.FieldValue.increment(1);
    this.afs.doc('settings/master').update({orderId: incrementBy1});
  }

  decrementMasterOrderNumber(){
    const decrementBy1 = firebase.firestore.FieldValue.increment(-11);
    this.afs.doc('settings/master').update({orderId: decrementBy1});
  }



  getUniqueMasterOrderNumber(): Promise<number>  {
    return new Promise((resolve, reject) => {
      const increment = firebase.firestore.FieldValue.increment(1);
      const program = this.afs.doc(`settings/master`);
      program.update({ orderId: increment }).then(t => {
        program.ref.get().then(function(doc) {
          if (doc.exists) {
            const orderId = doc.data().orderId;
            resolve(orderId);
          } else {
            reject(null);
          }
        })
        .catch(err => {
          reject(1);
        })
      })
      .catch(err => {
        reject(2);
      })
    });
  }

  getUniqueClinicOrderNumber(clinic_id): Promise<string>  {
    return new Promise((resolve, reject) => {
      const increment = firebase.firestore.FieldValue.increment(1);
      const program = this.afs.doc(`clinics/${clinic_id}`);
      program.update({ current_order: increment }).then(t => {
        program.ref.get().then(function(doc) {
          if (doc.exists) {
            resolve(doc.data().current_order);
          } else {
            reject(null);
          }
        })
        .catch(err => {
          reject(err);
        })
      })
      .catch(err => {
        reject(err);
      })
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/database/database.ts